import { useState } from 'react'
import { useAtomValue } from 'jotai'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded'

import { selectedMunicipalityIdAtom } from '@/state'
import { useDeleteYearlyFeePayment } from '@/hooks/api/use-yearly-fee-payment'

function RemoveDialog({
    yearlyFeePaymentId,
    handleClose,
}: {
    yearlyFeePaymentId: number,
    handleClose: () => void,
}) {

    const [requestInProgress, setRequestInProgress] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)

    const municipalityId = useAtomValue(selectedMunicipalityIdAtom)

    const mutation = useDeleteYearlyFeePayment(
        yearlyFeePaymentId,
        municipalityId,
        {
            setRequestInProgress,
            onErrorCallback(errorMessage) {
                setError(errorMessage)
            }
        },
    )

    const handleClickDelete = () => {
        setRequestInProgress(true)
        mutation.mutate()
    }

    return (
        <Box sx={{
            minWidth: 200,
            minHeight: 100,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
        }}>
            {error ?
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 2,
                    px: 3,
                }}>
                    <ErrorRoundedIcon sx={{
                        color: 'error.main',
                        fontSize: '4rem',
                    }}/>
                    <Typography variant="body1" component="div">
                        Platbu se nepodařilo smazat:
                    </Typography>
                    <Typography variant="body2" component="div">
                        {error}
                    </Typography>
                    <Button
                        variant="contained"
                        color="terciary"
                        size="small"
                        onClick={() => {
                            setError(null)
                            handleClose()
                        }}
                    >
                        {'Zavřít'}
                    </Button>
                </Box>
                :
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                    <Typography variant="h5" component="div" mb={2}>
                        Potvrďte prosím smazání platby ID: {yearlyFeePaymentId}
                    </Typography>
                    <Typography variant="body2" component="div" mb={2}>
                        Tato akce platbu vymaže z tohoto seznamu a dalších navázaných seznamů.
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        flexGrow: 1,
                    }}>
                        <Button
                            variant="text"
                            color="terciary"
                            onClick={() => handleClose()}
                        >
                            Zrušit
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            startIcon={requestInProgress ? <CircularProgress color="inherit" size={20} /> : <DeleteRoundedIcon />}
                            onClick={() => handleClickDelete()}
                            disabled={requestInProgress}
                        >
                            Smazat
                        </Button>
                    </Box>
                </Box>
            }
        </Box>
    )
}

export default RemoveDialog
